import { useRef } from "react";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import { useTranslation } from "react-i18next";
import { useOpenState } from "../../../../hooks/useOpenState";
import { styled } from "../../../../lib/styled";
import { Caption } from "../../../primitives/data-display/Caption";

interface StatedStudyTypeHintProps {
  /** The study type the paper states for itself (PubMed's own publication type). */
  statedPublicationType: string;
  /** The study type we classified the paper as, from its primary finding. */
  annotatedStudyType: string;
}

const HintButton = styled(IconButton)(({ theme }) => ({
  marginLeft: theme.spacing(0.5),
  padding: 0,
  verticalAlign: "text-bottom",
}));

const HintIcon = styled(HelpOutlineIcon)({ fontSize: 16 });

const HintBody = styled("div")(({ theme }) => ({
  maxWidth: 280,
  padding: theme.spacing(1.5),
}));

/** A "?" that explains why a paper's stated study type differs from the one we classified it as. */
export function StatedStudyTypeHint({
  statedPublicationType,
  annotatedStudyType,
}: StatedStudyTypeHintProps) {
  const { t } = useTranslation();
  const { open, setOpen, setClose } = useOpenState();
  const anchorRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <HintButton
        ref={anchorRef}
        size="small"
        aria-label={t("statedStudyType.label")}
        onClick={setOpen}
      >
        <HintIcon />
      </HintButton>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={setClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <HintBody>
          <Caption color="textPrimary">
            {t("statedStudyType.reason", {
              stated: statedPublicationType,
              annotated: annotatedStudyType,
            })}
          </Caption>
        </HintBody>
      </Popover>
    </>
  );
}
